<script setup>
import { computed, nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { Search01Icon, Tag01Icon } from '@hugeicons/core-free-icons';
import { vOnClickOutside } from '@vueuse/components';
import OptionSelect from 'dashboard/components-next/select/OptionSelect.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import HugeIcon from 'dashboard/components-next/icon/HugeIcon.vue';
import LabelPill from 'dashboard/components-next/label/LabelPill.vue';
import AddLabel from 'dashboard/routes/dashboard/settings/labels/AddLabel.vue';

const props = defineProps({
  modelValue: { type: Object, default: () => ({}) },
  options: { type: Array, default: () => [] },
  disabled: { type: Boolean, default: false },
  errorMessage: { type: String, default: '' },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();
const addLabelRef = ref(null);
const isLabelMenuOpen = ref(false);
const labelSearch = ref('');
const labelSearchInput = ref(null);

const operationOptions = computed(() => [
  { key: 'add', label: t('AUTOMATION.ACTIONS.ADD_TAG') },
  { key: 'remove', label: t('AUTOMATION.ACTIONS.REMOVE_TAG') },
]);

const normalizedModel = computed(() => ({
  operation: props.modelValue?.operation || 'add',
  tags: props.modelValue?.tags || [],
}));

const operation = computed({
  get: () => normalizedModel.value.operation,
  set: value =>
    emit('update:modelValue', { ...normalizedModel.value, operation: value }),
});

const tags = computed({
  get: () => normalizedModel.value.tags,
  set: value =>
    emit('update:modelValue', { ...normalizedModel.value, tags: value }),
});

const hasLabels = computed(() => props.options.length > 0);

const filteredLabels = computed(() => {
  const query = labelSearch.value.trim().toLowerCase();
  if (!query) return props.options;

  return props.options.filter(label =>
    label.name.toLowerCase().includes(query)
  );
});

const isLabelSelected = label =>
  tags.value.some(selectedLabel => selectedLabel.id === label.id);

const toggleLabel = label => {
  if (isLabelSelected(label)) {
    tags.value = tags.value.filter(
      selectedLabel => selectedLabel.id !== label.id
    );
    return;
  }

  tags.value = [
    ...tags.value,
    {
      id: label.id,
      name: label.name,
      emoji: label.emoji,
      color: label.color,
    },
  ];
};

const toggleLabelMenu = async () => {
  if (props.disabled) return;

  isLabelMenuOpen.value = !isLabelMenuOpen.value;
  if (isLabelMenuOpen.value) {
    await nextTick();
    labelSearchInput.value?.focus();
  } else {
    labelSearch.value = '';
  }
};

const closeLabelMenu = () => {
  isLabelMenuOpen.value = false;
  labelSearch.value = '';
};
</script>

<template>
  <div class="flex min-h-0 flex-1 flex-col gap-4">
    <div class="flex flex-col gap-1">
      <label class="text-xs font-medium leading-4 text-n-slate-11">
        {{ $t('AUTOMATION.ACTIONS.TAG_OPERATION') }}
      </label>
      <OptionSelect
        v-model="operation"
        :options="operationOptions"
        :disabled="disabled"
        :aria-label="$t('AUTOMATION.ACTIONS.TAG_OPERATION')"
      />
    </div>

    <div class="flex flex-col gap-2">
      <div
        v-if="hasLabels"
        v-on-click-outside="closeLabelMenu"
        class="relative min-h-8"
      >
        <div class="flex items-center justify-between gap-3">
          <label class="text-xs font-medium leading-4 text-n-slate-11">
            {{ $t('AUTOMATION.ACTIONS.LABELS') }}
          </label>
          <button
            type="button"
            :disabled="disabled"
            class="inline-flex size-8 shrink-0 items-center justify-center rounded-lg border border-n-weak text-n-slate-11 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12 disabled:cursor-not-allowed disabled:opacity-50 dark:border-n-strong dark:hover:bg-n-alpha-3"
            :aria-label="$t('AUTOMATION.ACTIONS.ADD_LABEL')"
            @click="toggleLabelMenu"
          >
            <span class="i-lucide-plus size-7" aria-hidden="true" />
          </button>
        </div>

        <div class="mt-2 flex min-h-8 flex-wrap items-center gap-2">
          <span
            v-for="label in tags"
            :key="label.id"
            class="inline-flex h-8 max-w-full items-center gap-1"
          >
            <LabelPill
              :label="{
                title: label.name,
                color: label.color,
                emoji: label.emoji,
              }"
              dense
            />
            <button
              type="button"
              :disabled="disabled"
              class="inline-flex size-4 shrink-0 items-center justify-center rounded text-n-slate-10 hover:text-n-slate-12 disabled:cursor-not-allowed"
              :aria-label="$t('AUTOMATION.ACTIONS.REMOVE_LABEL')"
              @click="toggleLabel(label)"
            >
              <span class="i-lucide-x size-3.5" aria-hidden="true" />
            </button>
          </span>
        </div>

        <div
          v-if="isLabelMenuOpen"
          class="absolute right-0 top-[4.5rem] z-20 flex w-72 max-w-full flex-col overflow-hidden rounded-xl border border-n-weak bg-n-solid-1 p-1 shadow-lg dark:border-n-strong dark:bg-n-solid-2"
        >
          <div
            class="flex h-9 items-center gap-2 rounded-lg border border-n-weak px-3 text-n-slate-12 transition-colors focus-within:border-n-brand dark:border-n-strong"
          >
            <HugeIcon
              :icon="Search01Icon"
              :size="18"
              class="pointer-events-none shrink-0 text-n-slate-10"
            />
            <input
              ref="labelSearchInput"
              v-model="labelSearch"
              type="search"
              class="reset-base min-w-0 flex-1 appearance-none border-0 bg-transparent p-0 text-sm text-n-slate-12 shadow-none outline-none ring-0 placeholder:text-n-slate-10 focus:border-0 focus:shadow-none focus:outline-none focus:ring-0"
              :placeholder="$t('AUTOMATION.ACTIONS.SEARCH_AND_SELECT_LABELS')"
            />
          </div>
          <div class="max-h-56 overflow-y-auto p-1">
            <p
              v-if="!filteredLabels.length"
              class="m-0 px-3 py-4 text-center text-sm text-n-slate-10"
            >
              {{ $t('LABEL_MGMT.NO_RESULTS') }}
            </p>
            <button
              v-for="label in filteredLabels"
              :key="label.id"
              type="button"
              class="flex w-full items-center gap-2 rounded-lg px-2 py-2 text-start text-sm text-n-slate-12 transition-colors hover:bg-n-alpha-2 dark:hover:bg-n-alpha-3"
              @click="toggleLabel(label)"
            >
              <Checkbox
                :model-value="isLabelSelected(label)"
                class="pointer-events-none shrink-0"
              />
              <span
                v-if="label.emoji"
                class="shrink-0 text-sm leading-none"
                aria-hidden="true"
              >
                {{ label.emoji }}
              </span>
              <HugeIcon
                v-else
                :icon="Tag01Icon"
                :size="14"
                class="shrink-0 text-n-slate-10"
              />
              <span class="truncate">{{ label.name }}</span>
            </button>
          </div>
        </div>
      </div>
      <label
        v-if="!hasLabels"
        class="text-xs font-medium leading-4 text-n-slate-11"
      >
        {{ $t('AUTOMATION.ACTIONS.LABELS') }}
      </label>
      <div
        v-if="!hasLabels"
        class="flex min-h-56 flex-col items-center justify-center rounded-xl border border-n-weak bg-n-solid-1 px-6 text-center dark:border-n-strong dark:bg-n-solid-2"
      >
        <div
          class="mb-4 flex size-12 items-center justify-center rounded-full bg-n-alpha-2"
        >
          <HugeIcon :icon="Tag01Icon" :size="24" class="text-n-slate-10" />
        </div>
        <p class="m-0 text-sm font-medium text-n-slate-12">
          {{ $t('AUTOMATION.ACTIONS.NO_LABELS_TITLE') }}
        </p>
        <p class="mt-1 text-xs text-n-slate-10">
          {{ $t('AUTOMATION.ACTIONS.NO_LABELS_DESCRIPTION') }}
        </p>
        <button
          type="button"
          :disabled="disabled"
          class="mt-4 inline-flex h-8 items-center gap-1.5 rounded-lg border border-n-weak px-3 text-sm font-medium text-n-slate-12 transition-colors hover:bg-n-alpha-2 disabled:cursor-not-allowed disabled:opacity-50 dark:border-n-strong dark:hover:bg-n-alpha-3"
          @click="addLabelRef?.open()"
        >
          <span class="i-lucide-plus size-4" aria-hidden="true" />
          {{ $t('AUTOMATION.ACTIONS.CREATE_LABEL') }}
        </button>
      </div>
      <span v-if="errorMessage" class="text-sm text-n-ruby-11">
        {{ errorMessage }}
      </span>
    </div>
    <AddLabel ref="addLabelRef" />
  </div>
</template>
